import { useBoolean, useToast } from '@chakra-ui/react'
import * as crypto from 'crypto-js'
import dayjs from 'dayjs'
import fileDownload from 'js-file-download'
import {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import useSessions from '@hook/shared/use-sessions'

import SapHistoricModalWrapper from '@component/Layout/Tools/SapHistoricModalWrapper'
import ScheduledPurschaseModalChart from '@component/Modules/Mrp/Modals/ScheduledChart'

import { parsePurchaseMrpScheduledData } from '@helper/grid/mrp/scheduled-purchase'
import { getErrorMessage } from '@helper/mrp/errorMessage'
import { AlteredSku, upsertAlteredSku } from '@helper/mrp/justifications'
import paramsHelper from '@helper/params'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange,
  mergeStoredHeaders
} from '@helper/util/cryptoStorange'
import { formattedDate } from '@helper/util/formattedDate'
import generic from '@service/generic'
import mrp from '@service/mrp'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import { IDefaultDate } from '@base/@types/providers/Alert/FollowUpProvider'
import {
  IMrpScheduledPurchaseContext,
  IParams,
  IUpdateParams,
  IUpdateParamsSap,
  IParamsSap,
  Discount,
  MrpScheduledPurchaseCalc
} from '@base/@types/providers/Mrp/ScheduledPurchaseProvider'
import mrpScheduledPurchaseHeader from '@base/variables/headers/mrp/scheduledPurchase'

const MrpScheduledPurchaseProvider: FC = ({ children }) => {
  const { register } = useSessions()
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const [defaoutDesiredPeriod, setDefaoutDesiredPeriod] = useState(1)
  const [currentyDate, setCurrentyDate] = useState({} as IDefaultDate)

  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('MRP Compra Programada')
  const [sapFlag, setSapFlag] = useState<boolean | null>(null)
  const [vmmFlag, setVmmFlag] = useState<boolean | null>(null)

  const [supplyMethod, updateSupplyMethod] = useImmer<any[]>([])
  const [selected, setSelected] = useState<any>()
  const [discountSupply, updateDiscountSupply] = useImmer<Discount>({
    id: 0,
    desconto: 0,
    fornecedorId: 0,
    metodoPagamentoId: 0
  })
  const [data, updateData] = useImmer<any[]>([])
  const [dataAux, updateDataAux] = useImmer<any[]>([])

  const [calc, updateCalc] = useImmer<MrpScheduledPurchaseCalc>({
    skusDistintos: 0,
    volumeTotalDeCompra: 0,
    custoTotalDeCompra: 0,
    custoRecomendado: 0
  })

  const defaultDate = useMemo(() => {
    const dateNow = new Date()
    const initialDate = dayjs(dateNow).format('YYYY-MM-DD')
    const finalDate = dayjs(new Date()).format('YYYY-MM-DD')

    setCurrentyDate({
      initialDate,
      finalDate
    })

    return {
      initialDate,
      finalDate
    } as IDefaultDate
  }, [])

  const [params, updateParams] = useImmer<IParams>({
    categoriasIds: [],
    categoriasCompactasIds: [],
    produtosIds: [],
    fornecedoresIds: [],
    gerenteCategoriaIds: [],
    gerenteProdutoIds: [],
    mundoIds: [],
    subCategoriaSelectIds: [],

    lojasIds: [],
    periodoDesejado: 1,
    centerTypes: [],
    utilizarVendaMediaMovel: 'Média Móvel',
    initialDate: formattedDate(defaultDate.initialDate)
  })

  const [currentOrderCode, setCurrentOrderCode] = useState('')
  const [alteredSkus, setAlteredSkus] = useImmer<AlteredSku[]>([])
  // Últimos parâmetros pesquisados (para recarregar após o envio do pedido)
  const appliedParamsRef = useRef<IParams | null>(null)
  // Trava contra envio duplicado do pedido ao SAP
  const isSubmittingRef = useRef(false)
  const [sendingOrder, setSendingOrder] = useState(false)

  const handleClearOrderCode = () => {
    setCurrentOrderCode('')
  }

  const isSapExportFlag = async () => {
    try {
      const { data: res } = await generic.getConfig(
        'FLAG_EXPORTAR_SAP_PROGRAMADA'
      )
      const { data } = res
      if (data?.valor === 'true') {
        return true
      } else {
        return false
      }
    } catch {
      return false
    }
  }

  const isVmmFlag = async () => {
    try {
      const { data: res } = await generic.getConfig('FLAG_VENDAMEDIAMOVEL')
      const { data } = res
      return data?.valor === 'true'
    } catch {
      return false
    }
  }

  const filtersList = useMemo<ILayoutFilter[]>(() => {
    return [
      ...(vmmFlag
        ? ([
            {
              id: 'utilizarVendaMediaMovel',
              label: 'Projeção',
              type: 'radio' as const,
              values: ['Lambda', 'Média Móvel']
            }
          ] as ILayoutFilter[])
        : []),
      {
        id: 'centerTypes',
        label: 'Tipo de Centro',
        filterId: 'centerTypes',
        type: 'select',
        isMulti: true
      },
      {
        id: 'lojasIds',
        label: 'Centro',
        filterId: 'centers',
        type: 'select',
        isMulti: true
      },
      // {
      //   id: 'gerenteCategoriaIds',
      //   label: 'Gerente de Categoria',
      //   filterId: 'categoryManagers',
      //   type: 'select',
      //   isMulti: true,
      //   isDisabled: params.produtosIds?.length > 0
      // },
      {
        id: 'gerenteProdutoIds',
        label: 'Gerente de Produto',
        filterId: 'productManagers',
        type: 'select',
        isMulti: true,
        isDisabled: params.produtosIds?.length > 0
      },
      // {
      //   id: 'mundoIds',
      //   label: 'Mundo',
      //   filterId: 'worlds',

      //   type: 'select',
      //   isMulti: true
      // },
      {
        id: 'categoriasIds',
        label: 'Categoria',
        filterId: 'categories',

        type: 'select',
        isMulti: true
      },
      {
        id: 'categoriasCompactasIds',
        label: 'Categoria Compacta',
        filterId: 'compactCategories',
        type: 'select',
        isMulti: true
      },
      {
        id: 'subCategoriaSelectIds',
        label: 'Sub Categoria',
        filterId: 'subCategories',

        type: 'select',
        isMulti: true
      },
      {
        id: 'produtosIds',
        label: 'Produto',
        type: 'select-api-post',
        apiUrl: '/v1/Produto/Compras',
        isMulti: true,
        isDisabled: params.fornecedoresIds.length > 0
      },
      {
        id: 'fornecedoresIds',
        label: 'Fornecedor',
        filterId: 'providersForPurchase',
        type: 'select',
        isMulti: true,
        isDisabled: params.produtosIds.length > 0
      },
      {
        id: 'periodoDesejado',
        label: 'Período desejado (em dias)',
        type: 'number-input',
        isRequired: true,
        defaultValue: 1,
        min: 1
      },
      {
        id: 'periodo',
        label: '',
        type: 'date-input-one',
        isMulti: true
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vmmFlag])

  // A configuração salva entra já na inicialização do estado. Restaurá-la
  // depois, em efeito ou durante a busca, descartava a seleção que o usuário
  // tinha acabado de aplicar.
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(() =>
    mergeStoredHeaders(
      mrpScheduledPurchaseHeader,
      decrypterHeaderStorange('mrpPurshaseScheduleMrp')
    )
  )

  // Recebe os headers a gravar: fechar sobre `headers` persistia o valor
  // anterior ao toggle que acabou de ser aplicado.
  const handleHeaderLocalStorange = useCallback((header: ITableHeader[]) => {
    localStorage.removeItem('mrpPurshaseScheduleMrp')
    encrypteHeaderStorange('mrpPurshaseScheduleMrp', header)
  }, [])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      // Recipe do immer: o editor aplica vários toggles no mesmo lote do React
      // e recipes encadeiam sobre o resultado anterior. Derivar o próximo valor
      // de `headers` fazia só o último toggle do lote valer.
      updateHeaders(draft => {
        const col = draft[index]?.columns?.[column]
        if (col) {
          col.show = !col.show
          // Marcador estável de "configurada pelo usuário". Não pode alternar
          // junto com `show`, senão o editor perde o grupo de vista.
          col.notShowColumn = true
        }
      })
    },
    [updateHeaders]
  )

  // Persiste a configuração sempre que ela muda. Na montagem regrava o mesmo
  // valor que acabou de ser restaurado, então é inofensivo.
  useEffect(() => {
    handleHeaderLocalStorange(headers)
  }, [headers, handleHeaderLocalStorange])

  const [paramsSap, updateSapParams] = useImmer<IParamsSap>({
    desconto: '',
    metodoPagamento: []
  })

  const handleUpdateSapParams = useCallback(
    ({ name, value }: IUpdateParamsSap) => {
      updateSapParams(draft => {
        draft[name] = value
        if (
          name === 'metodoPagamento' &&
          discountSupply.metodoPagamentoId === value[0]?.value
        ) {
          // Forma de pagamento padrão do fornecedor: aplica o desconto cadastrado
          draft.desconto = discountSupply.desconto.toString()
        }
        if (
          name === 'desconto' &&
          draft.metodoPagamento[0]?.value ===
            discountSupply.metodoPagamentoId &&
          Number(value) < Number(discountSupply.desconto)
        ) {
          // Desconto mínimo para a forma de pagamento padrão do fornecedor
          draft.desconto = discountSupply.desconto.toString()
        }
      })
    },
    [discountSupply, updateSapParams]
  )
  const getDiscountBySupply = useCallback(async () => {
    try {
      const { data: res } = await generic.getDiscountSupply({
        fornecedoresIds: [
          params.fornecedoresIds.length > 0
            ? params.fornecedoresIds[0].value
            : 0
        ]
      })

      const { data } = res

      updateDiscountSupply(data[0])

      // const newParamsSap = paramsSap
      // updateSapParams({ ...newParamsSap, desconto: data[0].desconto })
    } catch {}
  }, [updateDiscountSupply, params])

  const exportData = useCallback(async () => {
    Swal.fire({
      title: 'Criando Pedidos!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    setLoading(true)

    try {
      const purshase = data
      const command = {
        lojaConfiguracaoGrid: {
          codigo: headers[0].columns[0] ? headers[0].columns[0].show : true,
          nome: headers[0].columns[0] ? headers[0].columns[1].show : true
        },
        centroDistribuicaoConfiguracaoGrid: {
          codigo: true,
          nome: true
        },
        produtoConfiguracaoGrid: {
          material: true,
          loteMultiplo: false,
          intervaloCompra: headers[1].columns[9]
            ? headers[1].columns[9].show
            : true,
          codigo: headers[1].columns[0] ? headers[1].columns[0].show : true,
          nome: headers[1].columns[1] ? headers[1].columns[1].show : true,
          categoria: headers[1].columns[4] ? headers[1].columns[4].show : true,
          curvaABC: headers[1].columns[5] ? headers[1].columns[5].show : true,
          curvaABCEmpresa: headers[1].columns[6]
            ? headers[1].columns[6].show
            : true,
          status: headers[1].columns[7] ? headers[1].columns[7].show : true,
          leadTime: headers[1].columns[8] ? headers[1].columns[8].show : true,
          grupoSeparacao: headers[1].columns[10]
            ? headers[1].columns[10].show
            : true,
          MundoCodigo: false,
          MundoDescricao: headers[1].columns[2]
            ? headers[1].columns[2].show
            : true,
          CategoriaCompactaDescricao: headers[1].columns[3]
            ? headers[1].columns[3].show
            : true,
          CategoriaCompactaCodigo: false
        },
        fornecedorConfiguracaoGrid: {
          codigo: headers[2].columns[0] ? headers[2].columns[0].show : true,
          nome: headers[2].columns[1] ? headers[2].columns[1].show : true
        },

        recomendacaoConfiguracaoGrid: {
          pedidos: headers[3].columns[0] ? headers[3].columns[0].show : true,
          tipoAlerta: true,
          quantidade: headers[3].columns[1] ? headers[3].columns[1].show : true,
          peso: false,
          custo: headers[3].columns[3] ? headers[3].columns[3].show : true,
          volume: headers[3].columns[2] ? headers[3].columns[2].show : true,
          coberturaMedia: headers[3].columns[4]
            ? headers[3].columns[4].show
            : true,
          nivelServico: false
        },
        compraConfiguracaoGrid: {
          pedidos: headers[4].columns[0] ? headers[4].columns[0].show : true,
          quantidade: headers[4].columns[2] ? headers[4].columns[2].show : true,
          coberturaMedia: headers[4].columns[4]
            ? headers[4].columns[4].show
            : true,
          peso: headers[4].columns[0] ? headers[4].columns[0].show : true,
          volume: headers[4].columns[1] ? headers[4].columns[1].show : true,
          desejada: false,
          custo: headers[4].columns[3] ? headers[4].columns[3].show : true,
          nivelServico: false
        },

        estoqueConfiguracaoGrid: {
          max: headers[5].columns[0] ? headers[5].columns[0].show : true,
          cd: false,
          disponivel: headers[5].columns[2] ? headers[5].columns[2].show : true,
          reservado: headers[5].columns[1] ? headers[5].columns[1].show : true,
          transito: headers[5].columns[3] ? headers[5].columns[3].show : true,
          dep16: false,
          outros: headers[5].columns[4] ? headers[5].columns[4].show : true,
          vendaMediaDiaria: headers[5].columns[5]
            ? headers[5].columns[5].show
            : true,
          vendaMediaMensal: false
        },

        estoqueReabEmergencialConfiguracaoGrid: {
          estoqueAtual: true,
          pedidosEmTransito: true,
          coberturaAtual: true,
          curvaABC: true,
          estoqueCD: true,
          quantidadeSugerida: true,
          peso: true
        },

        coberturaConfiguracaoGrid: {
          alvo: true,
          atual: true,
          transito: true,
          sugestao: true,
          desejada: true,
          diferenca: true
        }
      }
      const { data: res } = await mrp.postPurshaseProgramedExport({
        params: {
          recomendacaoFornecimentoCommand: purshase,
          configuracaoGridCommand: command
        }
      })

      setLoading(false)
      Swal.close()
      fileDownload(res, filename + '.csv')
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      Swal.close()
      setLoading(false)
    }
  }, [data, toast, filename, headers])

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    if (sapFlag === null) return [] // Aguarda a flag ser carregada antes de definir ações

    return [
      {
        type: 'purchaseSuppliers-createOrder-schedule',
        data: data,
        onUpdate: null
      },
      // {
      //   type: 'sapHistoricPurchaseSchedul'
      // },
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'purshaseProgramedMrp',
            data,
            headers,
            parser: parsePurchaseMrpScheduledData
          },
          ...(sapFlag
            ? [
                {
                  type: 'sap' as const,
                  entity: 'purshaseProgramedMrp',
                  data,
                  sapModal: true
                }
              ]
            : [])
        ]
      },
      {
        type: 'columns',
        data: headers,
        onToggleColumn: toggleColumn
      },
      {
        type: 'purchaseSuppliersProgrammed',
        data: data,
        onUpdate: updateData
      }
    ]
  }, [toggleColumn, data, updateData, headers, sapFlag])

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'icon',
        icon: <AiOutlineBarChart />,
        onSelect: setSelected
      }
      // { type: 'iconCalculo' } — oculto, manter para ativar futuramente
    ]
  }, [])

  const clearAllFilters = () => {
    updateParams({
      ...params,
      categoriasIds: [],
      categoriasCompactasIds: [],
      produtosIds: [],
      fornecedoresIds: [],
      gerenteCategoriaIds: [],
      gerenteProdutoIds: [],
      mundoIds: [],
      centerTypes: [],
      lojasIds: [],
      periodoDesejado: 1,
      utilizarVendaMediaMovel: 'Média Móvel',
      initialDate: formattedDate(defaultDate.initialDate)
    })

    setDefaoutDesiredPeriod(1)
    if (localStorage.getItem('mrpScheduledPurchaseParams')) {
      localStorage.setItem(
        'mrpScheduledPurchaseParams',
        crypto.AES.encrypt(
          JSON.stringify({
            ...params,
            categoriasIds: [],
            categoriasCompactasIds: [],
            produtosIds: [],
            fornecedoresIds: [],
            gerenteCategoriaIds: [],
            gerenteProdutoIds: [],
            mundoIds: [],
            lojasIds: [],
            centerTypes: [],
            periodoDesejado: 1,
            initialDate: formattedDate(defaultDate.initialDate)
          }),
          'CHAVE'
        ).toString()
      )
    }
  }

  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    name === 'initialDate' &&
      setCurrentyDate({
        initialDate: value,
        finalDate: currentyDate.finalDate
      })
    name === 'periodoDesejado' && setDefaoutDesiredPeriod(value)

    updateParams(draft => {
      ;(draft as any)[name] = value
    })
    if (localStorage.getItem('mrpScheduledPurchaseParams')) {
      const newParams: IParams[] = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('mrpScheduledPurchaseParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )

      localStorage.setItem(
        'mrpScheduledPurchaseParams',
        crypto.AES.encrypt(
          JSON.stringify({ ...newParams, [name]: value }),
          'CHAVE'
        ).toString()
      )
    }
  }

  const startBackgroundPagination = useCallback(
    async (page: number, params: any) => {
      try {
        const { data: res } = await mrp.getPurschaseData({
          page,
          params
        })

        const { data, pagination } = res

        updateData(draft => {
          return draft.concat(data)
        })

        if (pagination.totalPages === page) {
          setFetching(false)
        } else {
          startBackgroundPagination(page + 1, params)
        }
      } catch {}
    },
    [updateData]
  )

  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('mrpScheduledPurchaseParams')) {
      const newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('mrpScheduledPurchaseParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )
      newParams.initialDate = defaultDate.initialDate
      newParams.utilizarVendaMediaMovel = 'Média Móvel'
      updateParams(newParams)
    } else {
      localStorage.setItem(
        'mrpScheduledPurchaseParams',
        crypto.AES.encrypt(JSON.stringify(params), 'CHAVE').toString()
      )
    }
  }, [defaultDate.initialDate, updateParams, params])

  const getSupplyCommands = useCallback(async () => {
    try {
      const { data: res } = await generic.getSupplyMethods()

      const { data } = res

      updateSupplyMethod(data)
    } catch {}
    setLoading(false)
  }, [updateSupplyMethod])

  // Busca o relatório. `isReload` recarrega após o envio do pedido sem alternar
  // o painel de filtros nem registrar a sessão novamente.
  const fetchReport = useCallback(
    async (searchParams: IParams, isReload = false) => {
      // Nova busca: alterações e dados auxiliares do gráfico são da busca anterior
      setAlteredSkus([])
      updateDataAux([])
      appliedParamsRef.current = searchParams
      setLoading(true)
      try {
        const paramsToSend = {
          ...searchParams,
          categoriasIds: paramsHelper.parse(searchParams.categoriasIds),
          categoriasCompactasIds: paramsHelper.parse(
            searchParams.categoriasCompactasIds
          ),
          produtosIds: paramsHelper.parse(searchParams.produtosIds),
          fornecedoresIds: paramsHelper.parse(searchParams.fornecedoresIds),
          gerenteProdutoIds: paramsHelper.parse(searchParams.gerenteProdutoIds),
          gerenteCategoriaIds: paramsHelper.parse(
            searchParams.gerenteCategoriaIds
          ),
          mundoIds: paramsHelper.parse(searchParams.mundoIds),
          lojasIds: paramsHelper.parse(searchParams.lojasIds),
          tiposCentros: paramsHelper.parse(searchParams.centerTypes),
          subCategoriasIds: paramsHelper.parse(
            searchParams.subCategoriaSelectIds
          ),
          utilizarVendaMediaMovel:
            !!vmmFlag && searchParams.utilizarVendaMediaMovel === 'Média Móvel',
          dataInicio:
            searchParams.initialDate || formattedDate(defaultDate.initialDate)
        }
        delete paramsToSend.initialDate

        const { data: res } = await mrp.getPurschaseProgramedData({
          page: 1,
          params: paramsToSend,
          isProgramada: true
        })
        const { data, pagination, message } = res
        updateData(draft => {
          return draft.filter(item => item.status === 99).concat(data)
        })
        if (searchParams.fornecedoresIds.length === 1) {
          getDiscountBySupply()
        }
        if (!isReload) {
          register({
            name: 'MRP Compra Programda',
            path: '/mrp/compra/programada',
            loading: false
          })
          setFilters.off()
        }
        if (pagination.totalPages > 1) {
          setFetching(true)
          startBackgroundPagination(2, paramsToSend)
        } else {
          setFetching(false)
        }
        if (!isReload) setFilters.toggle()
        if (message === 'ProdutosNaoParametrizados') {
          toast({
            title: 'Atenção',
            description:
              'Alguns produtos não trouxeram resultado por não estarem parametrizados corretamente',
            status: 'error',
            duration: 6000,
            isClosable: true
          })
        }
      } catch (error: any) {
        if (!isReload) setFilters.toggle()
        updateData([])
        const { status } = error?.response ?? {}

        if (!isReload) setFilters.toggle()
        if (status !== 400) {
          toast({
            title: 'Atenção',
            description: getErrorMessage(error),
            status: 'error',
            duration: 2000,
            isClosable: true
          })
        }
      }
      if (!isReload) setFilters.toggle()
      setLoading(false)
    },
    [
      vmmFlag,
      defaultDate.initialDate,
      setAlteredSkus,
      updateDataAux,
      setFilters,
      updateData,
      register,
      getDiscountBySupply,
      startBackgroundPagination,
      toast
    ]
  )

  const getData = useCallback(async () => {
    // A configuração de colunas é restaurada na inicialização do estado.
    // Recarregá-la aqui descartava a seleção recém-aplicada pelo usuário.
    const isDisabledProgress =
      params.lojasIds.length > 0 ||
      params.categoriasCompactasIds.length > 0 ||
      params.categoriasIds.length > 0 ||
      params.produtosIds.length > 0 ||
      params.fornecedoresIds.length > 0 ||
      params.mundoIds.length > 0
    if (!isDisabledProgress) {
      toast({
        title: 'Atenção',
        description: 'Selecionar ao menos um filtro',
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }
    const isPeriodoDesejadoValidate = params.periodoDesejado > 0
    if (!isPeriodoDesejadoValidate) {
      toast({
        title: 'Erro Requisição.',
        description: 'colocar numero maior que 0 em Período desejado',
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }

    Swal.fire({
      title: 'Deseja Prosseguir?',
      html: '<p style="font-size: 16px">Gostaria de informar que agora você tem duas opções de relatórios:</p> <p style="font-size: 16px">Gerar e baixar o arquivo Excel (CSV) direto ou prosseguir para o relatório desejado normalmente.</p>',
      icon: 'success',
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#1D5840',
      confirmButtonText: 'Exportar CSV direto',
      cancelButtonText: 'Ir para o relatório',
      allowOutsideClick: false
    }).then(async result => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Exportar dados!',
          html: 'Aguarde um momento<b></b>.',
          showCloseButton: true,

          timerProgressBar: true,
          didOpen: () => {
            Swal.showLoading()
          },
          allowOutsideClick: false
        })
        try {
          const paramsToSend = {
            ...params,
            categoriasIds: paramsHelper.parse(params.categoriasIds),
            categoriasCompactasIds: paramsHelper.parse(
              params.categoriasCompactasIds
            ),
            produtosIds: paramsHelper.parse(params.produtosIds),
            fornecedoresIds: paramsHelper.parse(params.fornecedoresIds),
            gerenteProdutoIds: paramsHelper.parse(params.gerenteProdutoIds),
            gerenteCategoriaIds: paramsHelper.parse(params.gerenteCategoriaIds),
            mundoIds: paramsHelper.parse(params.mundoIds),
            lojasIds: paramsHelper.parse(params.lojasIds),
            tiposCentros: paramsHelper.parse(params.centerTypes),
            subCategoriasIds: paramsHelper.parse(params.subCategoriaSelectIds),
            utilizarVendaMediaMovel:
              !!vmmFlag && params.utilizarVendaMediaMovel === 'Média Móvel',
            dataInicio:
              params.initialDate || formattedDate(defaultDate.initialDate)
          }
          delete paramsToSend.initialDate

          const { data: res } = await mrp.postPurshaseProgrammedExport({
            params: paramsToSend
          })
          Swal.close()
          setLoading(false)

          fileDownload(res, filename + '.csv')
        } catch (error: any) {
          Swal.close()

          toast({
            title: 'Atenção',
            description: getErrorMessage(error),
            status: error?.response?.status === 400 ? 'warning' : 'error',
            duration: 2000,
            isClosable: true
          })
        }
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        await fetchReport(params)
      }
    })
  }, [params, toast, defaultDate.initialDate, filename, vmmFlag, fetchReport])

  // Envia o pedido ao SAP (`unico` = pedido único). Resolve true em caso de sucesso.
  const sendOrderSap = useCallback(
    async (unico: boolean): Promise<boolean> => {
      // Trava contra envio duplicado (duplo clique)
      if (isSubmittingRef.current) return false

      const linhasValidasParaEnvio = data.filter(
        item => item.compraProgramaDesejada?.totalPedidos > 0
      )

      // 2. VALIDAÇÃO: agora a verificação é sobre o novo array filtrado
      if (linhasValidasParaEnvio.length === 0) {
        toast({
          title: 'Atenção',
          description:
            'Nenhuma (Compra Desejada) maior que zero encontrada para exportação',
          status: 'warning',
          duration: 4000,
          isClosable: true
        })
        return false
      }

      isSubmittingRef.current = true
      setSendingOrder(true)

      Swal.fire({
        title: 'Criando Pedidos!',
        html: 'Aguarde um momento<b></b>.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
        backdrop: true,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      // 3. ENVIAR apenas os dados filtrados, com justificativa dos SKUs alterados
      const alteredById = new Map(alteredSkus.map(sku => [sku.id, sku]))
      const purshase = linhasValidasParaEnvio.map(item => {
        const alteredSku = alteredById.get(item.estoque.id)
        if (alteredSku) {
          return {
            ...item,
            justificativa: alteredSku.justificativa,
            observacao: alteredSku.observacao
          }
        }
        return item
      })

      const obj = {
        recomendacaoFornecimentoCommand: purshase,
        desconto:
          Number(paramsSap.desconto) !== 0 ? Number(paramsSap.desconto) : null,
        formaDePagamento: paramsSap.metodoPagamento[0]
          ? paramsSap.metodoPagamento[0].value
          : null
      }
      try {
        const { data: res, status } = unico
          ? await mrp.postPurshaseProgramedUnicExportSap({ params: obj })
          : await mrp.postPurshaseProgramedExportSap({ params: obj })

        // Verificação manual do status de resposta e conteúdo
        if (
          status !== 200 ||
          res?.erro ||
          res?.error ||
          res?.statusCode >= 400
        ) {
          throw new Error(res?.message || 'Erro ao processar pedido')
        }

        updateSapParams({
          desconto: '',
          metodoPagamento: []
        })
        setCurrentOrderCode('108')
        // Itens já enviados: limpa as alterações e recarrega o relatório para
        // que os mesmos itens não sejam reenviados
        setAlteredSkus([])
        Swal.close()
        toast({
          title: 'Sucesso',
          description: 'Pedido criado com Sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        if (appliedParamsRef.current) {
          fetchReport(appliedParamsRef.current, true)
        }
        return true
      } catch (error: any) {
        // Mantém forma de pagamento/desconto para o usuário poder tentar de novo
        Swal.close()
        toast({
          title: 'Atenção',
          description: getErrorMessage(error),
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
        setLoading(false)
        return false
      } finally {
        isSubmittingRef.current = false
        setSendingOrder(false)
      }
    },
    [
      data,
      alteredSkus,
      toast,
      paramsSap.desconto,
      paramsSap.metodoPagamento,
      updateSapParams,
      setAlteredSkus,
      fetchReport
    ]
  )

  const exportDataSap = useCallback(() => sendOrderSap(false), [sendOrderSap])

  const exportUnicDataSap = useCallback(
    () => sendOrderSap(true),
    [sendOrderSap]
  )

  const exportDataSapCsv = useCallback(async () => {
    Swal.fire({
      title: 'Exporta dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const purshase = data

    let obj = {}
    if (params.fornecedoresIds.length === 1) {
      obj = {
        recomendacaoFornecimentoCommand: purshase,
        desconto:
          Number(paramsSap.desconto) !== 0 ? Number(paramsSap.desconto) : null,
        formaDePagamento: paramsSap.metodoPagamento[0]
          ? paramsSap.metodoPagamento[0].value
          : null
      }
    } else {
      obj = {
        recomendacaoFornecimentoCommand: purshase,
        desconto:
          Number(paramsSap.desconto) !== 0 ? Number(paramsSap.desconto) : null,
        formaDePagamento: paramsSap.metodoPagamento[0]
          ? paramsSap.metodoPagamento[0].value
          : null
      }
    }
    try {
      const { data: res } = await mrp.postPurshaseProgramedExportSapCsv({
        params: obj
      })
      fileDownload(res, filename + '.csv')
      Swal.close()
      updateSapParams({
        desconto: '',
        metodoPagamento: []
      })
    } catch (error: any) {
      updateSapParams({
        desconto: '',
        metodoPagamento: []
      })
      Swal.close()

      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })

      setLoading(false)
      Swal.close()
    }
  }, [
    data,
    params.fornecedoresIds.length,
    paramsSap.desconto,
    paramsSap.metodoPagamento,
    filename,
    updateSapParams,
    toast
  ])

  const exportUnicDataSapCsv = useCallback(async () => {
    const purshase = data

    let obj = {}
    if (params.fornecedoresIds.length === 1) {
      obj = {
        recomendacaoFornecimentoCommand: purshase,
        desconto:
          Number(paramsSap.desconto) !== 0 ? Number(paramsSap.desconto) : null,
        formaDePagamento: paramsSap.metodoPagamento[0]
          ? paramsSap.metodoPagamento[0].value
          : null
      }
    } else {
      obj = {
        recomendacaoFornecimentoCommand: purshase,
        desconto:
          Number(paramsSap.desconto) !== 0 ? Number(paramsSap.desconto) : null,
        formaDePagamento: paramsSap.metodoPagamento[0]
          ? paramsSap.metodoPagamento[0].value
          : null
      }
    }
    try {
      const { data: res } = await mrp.postPurshaseProgramedUnicExportSapCsv({
        params: obj
      })
      updateSapParams({
        desconto: '',
        metodoPagamento: []
      })
      updateSapParams({
        desconto: '',
        metodoPagamento: []
      })
      fileDownload(res, filename + '.csv')
    } catch (error: any) {
      updateSapParams({
        desconto: '',
        metodoPagamento: []
      })
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
    }
  }, [
    data,
    params.fornecedoresIds.length,
    paramsSap.desconto,
    paramsSap.metodoPagamento,
    updateSapParams,
    filename,
    toast
  ])
  const calculate = useCallback(() => {
    const distinctSkus: any[] = []
    let customRecomendadoSum = 0
    let custoTotalDeCompraSum = 0
    let volumeTotalDeCompra = 0
    for (const row of data) {
      customRecomendadoSum += row.compraProgramaRecomendada.custoTotal
      custoTotalDeCompraSum += row.compraProgramaDesejada.custoTotal
      volumeTotalDeCompra += row.compraProgramaDesejada.volumeTotal

      const idFornecedor = row.estoque.produtoFornecedor.fornecedor
        ? row.estoque.produtoFornecedor.fornecedor.id
        : 0
      const idProduto = row.estoque.produtoFornecedor.produto
        ? row.estoque.produtoFornecedor.produto.id
        : 0
      const checkOne = !!distinctSkus.filter(
        x => x.supplier === idFornecedor && x.product === idProduto
      )
      const checkTwo = row.compraProgramaRecomendada.totalPedidos > 0

      if (checkOne && checkTwo) {
        distinctSkus.push({
          supplier: row.estoque.produtoFornecedor.fornecedor
            ? row.estoque.produtoFornecedor.fornecedor.id
            : 0,
          product: row.estoque.produtoFornecedor.produto
            ? row.estoque.produtoFornecedor.produto.id
            : 0
        })
      }
    }
    updateCalc(draft => {
      draft.skusDistintos = distinctSkus.length
      draft.volumeTotalDeCompra = volumeTotalDeCompra
      draft.custoTotalDeCompra = custoTotalDeCompraSum
      draft.custoRecomendado = customRecomendadoSum
    })
  }, [updateCalc, data])

  const handleUpdateWanted = useCallback(
    async (
      newEntity: any,
      quant: number,
      price: number,
      date: any
    ): Promise<number | undefined> => {
      let purchases = []
      if (dataAux.length > 0) {
        purchases = dataAux
      } else {
        purchases = data
      }
      // eslint-disable-next-line prefer-const
      let purchase = purchases.filter(
        x =>
          x.estoque.loja.id === Number(newEntity.estoque.loja.id) &&
          x.estoque.produtoFornecedor.produto.id ===
            Number(newEntity.estoque.produtoFornecedor.produto.id)
      )[0]

      let quantidadePacote = purchase.estoque.produtoFornecedor.quantidadePacote
      quantidadePacote = quantidadePacote === 0 ? 1 : quantidadePacote
      const pacoteModulo = quant % quantidadePacote
      const pacoteDivisao = Math.floor(quant / quantidadePacote)
      if (pacoteModulo >= quantidadePacote / 2) {
        quant = (pacoteDivisao + 1) * quantidadePacote
      } else {
        quant = pacoteDivisao * quantidadePacote
      }
      quant = parseInt(quant.toString())

      // remove
      // const drpSimulator =
      //   purchase?.compraProgramaDesejada?.simulacaoDRP?.filter(
      //     (row: any) => row.diaDePedido && row.data === date
      //   )[0]

      const drpIndex = purchase?.compraProgramaDesejada.simulacaoDRP.findIndex(
        (row: any) => row.diaDePedido && row.data === date
      )

      // calculo
      const dateNew = new Date(date)
      dateNew.setDate(dateNew.getDate() + purchase.estoque.tempoDeEspera)
      const dateAlteracao = dateNew.getTime()
      // date obj
      const newDrp: any[] = []

      purchase.compraProgramaDesejada?.simulacaoDRP?.forEach((el: any) => {
        const dtLaco = Date.parse(el.data)
        let finalDisponivel = el.finalDisponivel
        if (dtLaco >= dateAlteracao) {
          finalDisponivel = el.finalDisponivel + quant
        }
        newDrp.push({
          ...el,
          finalDisponivel: finalDisponivel
        })
      })
      let medEstoque = 0
      newDrp.map((item: any) => (medEstoque += item.finalDisponivel))
      const calculomedia =
        medEstoque / purchase.compraProgramaDesejada?.simulacaoDRP.length

      // new drp
      const newDrpnew = newDrp[drpIndex]
      newDrp[drpIndex] = {
        data: newDrpnew.data,
        demandaPrevista: newDrpnew.demandaPrevista,
        comecoDisponivelMaisRecibos: newDrpnew.comecoDisponivelMaisRecibos,
        comecoDisponivel: newDrpnew.comecoDisponivel,
        unidadeRealVendida: newDrpnew.unidadeRealVendida,
        perdeuVenda: newDrpnew.perdeuVenda,
        foraDeEstoque: newDrpnew.foraDeEstoque,
        unidadeVendaPerdida: newDrpnew.unidadeVendaPerdida,
        temPedidos: newDrpnew.temPedidos,
        unidadePedido: quant,
        pedidosRecebidos: newDrpnew.pedidosRecebidos,
        unidadePedidoRecebido: newDrpnew.unidadePedidoRecebido,
        vendido: newDrpnew.vendido,
        finalDisponivel: newDrpnew.finalDisponivel,
        diaDePedido: newDrpnew.diaDePedido,
        requisitosLiquidosSobreLTRP: newDrpnew.requisitosLiquidosSobreLTRP
      }
      try {
        const idData = purchases.findIndex(
          (x: any) =>
            x.estoque.produtoFornecedor.produto.id ===
            Number(newEntity.estoque.produtoFornecedor.produto.id)
        )
        // VARIABLES DRP
        let neworder = 0

        newDrp
          .filter((x: any) => x.diaDePedido && x.unidadePedido !== 0)
          .map((item: any) => (neworder += item.unidadePedido))
        const numberOrder = newDrp.filter(
          (x: any) => x.diaDePedido && x.unidadePedido !== 0
        ).length

        purchases = [...purchases] // important to create a copy, otherwise you'll modify state outside of setState call
        purchases[idData] = {
          ...purchase,
          compraProgramaDesejada: {
            totalPedidos: neworder,
            numeroDePedidos: numberOrder,
            estoqueMedio: calculomedia,
            demandaMediaDiaria:
              purchase?.compraProgramaDesejada.demandaMediaDiaria,
            coberturaMedia:
              calculomedia /
              purchase?.compraProgramaDesejada.demandaMediaDiaria,
            custoTotal:
              neworder *
              purchase?.estoque?.custoUnitario *
              purchase?.estoque?.produtoFornecedor?.unidadeFornecedor
                .fatorConversao,
            volumeTotal:
              purchase?.estoque?.produtoFornecedor?.produto?.volume?.valor *
              neworder,
            inicio: purchase?.compraProgramaDesejada.inicio,
            fim: purchase?.compraProgramaDesejada.fim,
            simulacaoDRP: newDrp
          },
          desejada: {
            quantidade: purchase.desejada.quantidade + quant,
            cobertura: purchase.desejada.cobertura,
            coberturaAoAdicionarUmPacote:
              purchase.desejada.coberturaAoAdicionarUmPacote,
            custo: purchase.desejada.custo + price,
            volume: purchase.desejada.volume,
            peso: purchase.desejada.peso,
            pedidos: purchase.desejada.pedidos,
            medidaVenda: purchase.desejada.medidaVenda,
            medidaEstoque: purchase.desejada.mediaEstoque,
            nivelServico: purchase.desejada.nivelServico
          }
        }
        const newData = purchases
        updateData(newData)
        updateDataAux(newData)

        // SKU alterado: referência é a quantidade recomendada; voltando a ela deixa de ser alterado
        const alteredSku = {
          id: purchase.estoque.id,
          loja: purchase.estoque.loja,
          produto: purchase.estoque.produtoFornecedor.produto,
          idEmpresa: purchase.estoque.idEmpresa,
          quantidadeOriginal:
            purchase?.compraProgramaRecomendada?.totalPedidos ?? 0,
          novaQuantidade: neworder
        }
        setAlteredSkus(draft => {
          upsertAlteredSku(draft, alteredSku)
        })

        // Quantidade aplicada (arredondada ao pacote) para a linha do gráfico
        return quant
      } catch (error: any) {
        toast({
          title: 'Atenção',
          description: getErrorMessage(error),
          status: 'error',
          duration: 2000,
          isClosable: true
        })
        return undefined
      }
    },
    [dataAux, data, updateData, updateDataAux, setAlteredSkus, toast]
  )

  const handleUpdateJustification = useCallback(
    (skuId: number, justification: string, observation: string) => {
      setAlteredSkus(draft => {
        const sku = draft.find(item => item.id === skuId)
        if (sku) {
          sku.justificativa = justification
          sku.observacao = observation
        }
      })
    },
    [setAlteredSkus]
  )

  const handleDataAux = useCallback(async () => {
    if (dataAux.length === 0) {
      return
    }
    updateData(draft => {
      return draft.filter(item => item.status === 99).concat(dataAux)
    })
  }, [dataAux, updateData])

  useEffect(() => {
    if (data.length > 0) {
      calculate()
    }
  }, [data, calculate])

  useEffect(() => {
    const fetchSapFlag = async () => {
      const flag = await isSapExportFlag()
      setSapFlag(flag)
    }
    const fetchVmmFlag = async () => {
      const flag = await isVmmFlag()
      setVmmFlag(flag)
    }
    fetchSapFlag()
    fetchVmmFlag()
  }, [])

  return (
    <MrpScheduledPurchaseContext.Provider
      value={{
        loading,
        fetching,

        filtersList,
        tableInteraction,
        defaoutDesiredPeriod,

        originalData: data,
        data: parsePurchaseMrpScheduledData(data),
        actions,
        params,
        calc,
        selected,

        headers,
        defaultDate,
        currentyDate,
        filename,
        query,
        filters,
        getSupplyCommands,
        toggleColumn,
        handleUpdateParams,
        clearAllFilters,

        getData,
        exportData,
        setSelected,
        setQuery,
        setFilters,
        handleLocalParams,
        setFilename,
        exportDataSap,
        exportUnicDataSap,
        exportDataSapCsv,
        exportUnicDataSapCsv,
        supplyMethod,
        discountSupply,
        handleUpdateWanted,
        handleDataAux,
        handleUpdateSapParams,
        paramsSap,
        alteredSkus,
        sendingOrder,
        handleUpdateJustification
      }}
    >
      {children}
      <SapHistoricModalWrapper
        orderCode={currentOrderCode}
        onModalClosed={handleClearOrderCode}
      />
      <ScheduledPurschaseModalChart />
    </MrpScheduledPurchaseContext.Provider>
  )
}

export default MrpScheduledPurchaseProvider
export const MrpScheduledPurchaseContext =
  createContext<IMrpScheduledPurchaseContext>(
    {} as IMrpScheduledPurchaseContext
  )
